/**
 * Custom hook for managing PDFViewerPanel metadata
 *
 * Extracted from PDFViewerPanel.tsx
 */

import { useState, useEffect } from 'react';
import logger from '../../../utils/simpleLogger';
import { parseSavedMessageMarkdown } from '../../../utils/savedMessageMarkdown';
import { parseSnippetMarkdown } from '../../../utils/snippetMarkdown';
import { parseStudioMarkdown } from '../../../utils/studioMarkdown';
import type {
  UsePDFViewerPanelMetadataProps,
  UsePDFViewerPanelMetadataReturn,
} from '../types/pdf-viewer-panel.types';

/**
 * Manages metadata parsing and state for PDFViewerPanel
 */
export function usePDFViewerPanelMetadata({
  citationToView,
}: UsePDFViewerPanelMetadataProps): UsePDFViewerPanelMetadataReturn {
  const [savedMessageMetadata, setSavedMessageMetadata] = useState<Record<string, any>>({});
  const [studioMetadata, setStudioMetadata] = useState<Record<string, any>>({});

  // Debug: Log citation data only when it changes (not on every render)
  useEffect(() => {
    if (citationToView) {
      logger.info('📄 PDFViewerPanel - citationToView:', citationToView);
      logger.info('   ✅ document_id:', citationToView.document_id);
    }
  }, [citationToView]);

  // Actualizar metadata cuando cambia citationToView
  useEffect(() => {
    if (citationToView) {
      const detailMetadata = citationToView.metadata ?? {};

      logger.info('📋 PDFViewerPanel - detailMetadata:', {
        type: detailMetadata?.type,
        scope: detailMetadata?.scope,
        allKeys: Object.keys(detailMetadata),
      });

      const isSavedMessage = detailMetadata?.type === 'saved-message';
      const isStudioResult = detailMetadata?.type === 'studio-result';

      if (isSavedMessage) {
        setSavedMessageMetadata((detailMetadata?.front_matter as Record<string, any>) || {});
      } else {
        setSavedMessageMetadata({});
      }

      if (isStudioResult) {
        setStudioMetadata((detailMetadata?.front_matter as Record<string, any>) || {});
      } else {
        setStudioMetadata({});
      }
    } else {
      setSavedMessageMetadata({});
      setStudioMetadata({});
    }
  }, [citationToView]);

  // Resolver document_id y project_id
  const metadataDocumentId = citationToView?.metadata?.document_id as string | undefined;
  const metadataProjectId = citationToView?.metadata?.project_id as string | undefined;
  const resolvedDocumentId = citationToView?.document_id || metadataDocumentId || undefined;
  const resolvedProjectId = citationToView?.project_id || metadataProjectId || undefined;

  // Log para debugging
  if (citationToView && !resolvedDocumentId) {
    console.warn('⚠️ PDFViewerPanel: document_id no disponible', {
      'citationToView.document_id': citationToView.document_id,
      'metadata.document_id': metadataDocumentId,
      'documentPath': citationToView.documentPath,
      'metadata': citationToView.metadata
    });
  }

  const hasDocument = !!citationToView?.documentPath;
  const detailMetadata = citationToView?.metadata ?? {};
  const isStudioResult = detailMetadata?.type === 'studio-result';
  const isSnippet = detailMetadata?.type === 'snippet';
  const isSavedMessage = detailMetadata?.type === 'saved-message';

  // Parse markdown según tipo
  const detailTitle = citationToView?.snippetTitle;
  const detailText = citationToView?.snippetText;

  const savedMessageMarkdown = isSavedMessage
    ? (typeof detailMetadata?.markdown === 'string' ? detailMetadata.markdown : citationToView?.snippetText ?? detailText ?? '')
    : '';

  const snippetMarkdown = isSnippet
    ? (typeof detailMetadata?.markdown === 'string' ? detailMetadata.markdown : citationToView?.snippetText ?? detailText ?? '')
    : '';

  const studioMarkdown = isStudioResult
    ? (typeof detailMetadata?.markdown === 'string' ? detailMetadata.markdown : citationToView?.snippetText ?? detailText ?? '')
    : '';

  return {
    savedMessageMetadata,
    setSavedMessageMetadata,
    studioMetadata,
    setStudioMetadata,
    resolvedDocumentId,
    resolvedProjectId,
    hasDocument,
    isStudioResult,
    isSnippet,
    isSavedMessage,
    detailMetadata,
    detailTitle,
    detailText,
    savedMessageMarkdown,
    snippetMarkdown,
    studioMarkdown,
  };
}
